import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Star, Building, User, Calendar, Mail, Copy, ExternalLink, PlayCircle, StopCircle } from "lucide-react";
import { JobEvaluationListItem, isFeedbackDataV2 } from "@/types/avaliacao";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { createLocalDate } from "@/utils/dateUtils";
import { apiFetch } from "@/lib/api-fetch";
import { reportApiError } from "@/lib/report-error";
import { toUserError } from "@/lib/api-error";

interface AvaliacaoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  avaliacao: JobEvaluationListItem | null;
  evaluationData?: {
    rating?: number;
    feedback_text?: string;
    feedback_data?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    status?: string;
  };
}

export function AvaliacaoDialog({ isOpen, onClose, avaliacao, evaluationData }: AvaliacaoDialogProps) {
  const { toast } = useToast();
  const [generatedLinks, setGeneratedLinks] = useState<{
    freelancer?: string;
    agency?: string;
  }>({});

  // Limpar links gerados quando o dialog fechar
  useEffect(() => {
    if (!isOpen) {
      setGeneratedLinks({});
    }
  }, [isOpen]);

  if (!avaliacao) return null;

  // Cores diferentes para cada tipo de avaliador (igual ao AvaliacaoCard)
  const getDialogStyle = () => {
    if (avaliacao.evaluator_type === 'agency') {
      return 'border-l-4 border-l-blue-500';
    } else {
      return 'border-l-4 border-l-green-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'email_sent':
        return 'Email Enviado';
      case 'rated':
        return 'Avaliado';
      case 'completed':
        return 'Completo';
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'pending':
        return 'outline';
      case 'email_sent':
        return 'secondary';
      case 'rated':
        return 'default';
      case 'completed':
        return 'default';
      default:
        return 'outline';
    }
  };

  const getEvaluatorInfo = () => {
    if (avaliacao.evaluator_type === 'agency') {
      return {
        evaluator: {
          name: avaliacao.agencia_name || 'Agência não identificada',
          icon: Building,
          label: 'Agência',
          type: 'agency' as const
        },
        evaluated: {
          name: avaliacao.freelancer_name || 'Freelancer não identificado',
          icon: User,
          label: 'Freelancer',
          type: 'freelancer' as const
        }
      };
    } else {
      return {
        evaluator: {
          name: avaliacao.freelancer_name || 'Freelancer não identificado',
          icon: User,
          label: 'Freelancer',
          type: 'freelancer' as const
        },
        evaluated: {
          name: avaliacao.agencia_name || 'Agência não identificada',
          icon: Building,
          label: 'Agência',
          type: 'agency' as const
        }
      };
    }
  };

  const renderStars = (rating?: number) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
          <Star 
            key={star} 
            className={`h-4 w-4 ${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-300'
            }`} 
          />
        ))}
        <span className="ml-2 text-lg font-semibold">{rating}/10</span>
      </div>
    );
  };

  const gerarLinkAvaliacao = async (evaluatorType: 'freelancer' | 'agency') => {
    try {
      if (!avaliacao.controle_projeto_id) {
        toast({
          title: "Erro",
          description: "ID do projeto não encontrado. Não é possível gerar o link.",
          variant: "destructive",
        });
        return;
      }

      const data = await apiFetch<{ url: string }>(
        `${SUPABASE_URL}jobs/evaluations/generate-link/${avaliacao.controle_projeto_id}`,
        {
          method: "POST",
          body: JSON.stringify({ evaluator_type: evaluatorType }),
        },
      );
      
      // Tentar copiar para clipboard
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(data.url);
        } else {
          // Fallback para navegadores mais antigos
          const textArea = document.createElement('textarea');
          textArea.value = data.url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        }
        
        toast({
          title: "Link copiado!",
          description: `Link de avaliação para ${evaluatorType === 'freelancer' ? 'freelancer' : 'agência'} copiado para a área de transferência.`,
        });
      } catch (clipboardError) {
        console.error('Erro ao copiar para clipboard:', clipboardError);
        
        // Armazenar o link no estado para exibir abaixo do botão
        setGeneratedLinks(prev => ({
          ...prev,
          [evaluatorType]: data.url
        }));
        
        toast({
          title: "Link gerado!",
          description: "Não foi possível copiar automaticamente. " + clipboardError.message,
          duration: 5000,
        });
      }
    } catch (error) {
      reportApiError(error, { source: "avaliacoes", kind: "generate_link" });
      console.error('Erro ao gerar link:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o link de avaliação. " + toUserError(error).description,
        variant: "destructive",
      });
    }
  };

  const enviarEmailAvaliacao = async (evaluatorType: 'freelancer' | 'agency') => {
    try {
      if (!avaliacao.controle_projeto_id) {
        toast({
          title: "Erro",
          description: "ID do projeto não encontrado. Não é possível enviar o email.",
          variant: "destructive",
        });
        return;
      }

      await apiFetch(
        `${SUPABASE_URL}jobs/evaluations/send-email/${avaliacao.controle_projeto_id}`,
        {
          method: "POST",
          body: JSON.stringify({
            evaluator_type: evaluatorType,
            to_email: evaluatorType === "freelancer" ? avaliacao.freelancer_email : avaliacao.agencia_email,
          }),
        },
      );

      toast({
        title: "Email enviado!",
        description: `Email de avaliação enviado para ${evaluatorType === 'freelancer' ? 'o freelancer' : 'a agência'}.`,
      });
    } catch (error) {
      reportApiError(error, { source: "avaliacoes", kind: "send_email" });
      console.error('Erro ao enviar email:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar o email de avaliação. " + toUserError(error).description,
        variant: "destructive",
      });
    }
  };

  const { evaluator, evaluated } = getEvaluatorInfo();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${getDialogStyle()}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Star className="h-5 w-5 text-yellow-500" />
            Detalhes da Avaliação
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header da Avaliação */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <evaluator.icon className={`h-5 w-5 ${
                      avaliacao.evaluator_type === 'agency' ? 'text-blue-600' : 'text-green-600'
                    }`} />
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        {evaluator.label} (Avaliador)
                      </p>
                      <p className="font-semibold">{evaluator.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-2xl">→</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <evaluated.icon className={`h-5 w-5 ${
                      avaliacao.evaluator_type === 'agency' ? 'text-green-600' : 'text-blue-600'
                    }`} />
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        {evaluated.label} (Avaliado)
                      </p>
                      <p className="font-semibold">{evaluated.name}</p>
                    </div>
                  </div>
                </div>

                <Badge variant={getStatusVariant(avaliacao.status)} className="text-sm">
                  {getStatusLabel(avaliacao.status)}
                </Badge>
              </div>

              {/* Datas do Projeto */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {avaliacao.project_start_date && (
                  <div className="flex items-center gap-2">
                    <PlayCircle className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Início</p>
                      <p className="font-medium">
                        {format(createLocalDate(avaliacao.project_start_date), 'dd/MM/yyyy', { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                )}

                {avaliacao.project_end_date && (
                  <div className="flex items-center gap-2">
                    <StopCircle className="h-4 w-4 text-red-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Conclusão</p>
                      <p className="font-medium">
                        {format(createLocalDate(avaliacao.project_end_date), 'dd/MM/yyyy', { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {avaliacao.rated_at ? 'Avaliado' : 'Criado'}
                    </p>
                    <p className="font-medium">
                      {avaliacao.rated_at ? (
                        formatDistanceToNow(new Date(avaliacao.rated_at), { 
                          addSuffix: true, 
                          locale: ptBR 
                        })
                      ) : avaliacao.created_at ? (
                        formatDistanceToNow(new Date(avaliacao.created_at), { 
                          addSuffix: true, 
                          locale: ptBR 
                        })
                      ) : (
                        'Data não disponível'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avaliação */}
          {(avaliacao.rating || avaliacao.media_geral || evaluationData?.rating) ? (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  Avaliação Recebida
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Coluna 1: Nota e Comentário */}
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 font-medium">Nota Geral:</p>
                      {renderStars(avaliacao.media_geral ?? avaliacao.rating ?? evaluationData?.rating)}
                    </div>

                    {(evaluationData?.feedback_text) && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2 font-medium">Comentário:</p>
                        <p className="text-sm bg-muted p-3 rounded-lg">
                          {evaluationData.feedback_text}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Coluna 2: Detalhes Específicos */}
                  {evaluationData?.feedback_data && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-4 font-medium">Detalhes da Avaliação:</p>
                      <div className="space-y-4">
                        {avaliacao.evaluator_type === 'freelancer' ? (
                          isFeedbackDataV2(evaluationData.feedback_data) ? (
                            <>
                              {/* Rating principal como detalhe */}
                              {(avaliacao.rating || evaluationData.rating) && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Probabilidade de Recomendar</span>
                                  <span className="font-semibold text-primary">{avaliacao.rating ?? evaluationData.rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.gestao_geral_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Gestão Geral</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.gestao_geral_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.gestores_diretos_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Gestores Diretos</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.gestores_diretos_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.qualidade_feedbacks_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Qualidade e clareza dos feedbacks</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.qualidade_feedbacks_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.espaco_plataformas_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Espaço físico e/ou plataformas</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.espaco_plataformas_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.avaliacao_equipe_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Avaliação da Equipe</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.avaliacao_equipe_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.comunicacao_rh_financeiro_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Comunicação com RH e Financeiro</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.comunicacao_rh_financeiro_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.corisco_satisfaction_rating && (
                                <div className="flex items-center justify-between py-2">
                                  <span className="text-sm">Suporte da Corisco</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.corisco_satisfaction_rating}/10</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {/* V1: labels originais */}
                              {(avaliacao.rating || evaluationData.rating) && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Probabilidade de Recomendar</span>
                                  <span className="font-semibold text-primary">{avaliacao.rating ?? evaluationData.rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.service_satisfaction_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Satisfação com o serviço</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.service_satisfaction_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.team_evaluation_rating && (
                                <div className="flex items-center justify-between py-2 border-b border-border/30">
                                  <span className="text-sm">Avaliação da equipe</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.team_evaluation_rating}/10</span>
                                </div>
                              )}
                              {evaluationData.feedback_data.corisco_satisfaction_rating && (
                                <div className="flex items-center justify-between py-2">
                                  <span className="text-sm">Satisfação com a Corisco</span>
                                  <span className="font-semibold text-primary">{evaluationData.feedback_data.corisco_satisfaction_rating}/10</span>
                                </div>
                              )}
                            </>
                          )
                        ) : (
                          <>
                            {evaluationData.feedback_data.freelancer_performance_rating && (
                              <div className="flex items-center justify-between py-2 border-b border-border/30">
                                <span className="text-sm">Performance do freelancer</span>
                                <span className="font-semibold text-primary">{evaluationData.feedback_data.freelancer_performance_rating}/10</span>
                              </div>
                            )}
                            {evaluationData.feedback_data.freelancer_characteristics && evaluationData.feedback_data.freelancer_characteristics.length > 0 && (
                              <div className="py-2 border-b border-border/30">
                                <div className="flex items-start justify-between gap-3">
                                  <span className="text-sm">Características destacadas</span>
                                  <div className="flex flex-wrap gap-1 justify-end">
                                    {evaluationData.feedback_data.freelancer_characteristics.map((char: string, index: number) => (
                                      <span key={index} className="inline-block bg-primary/10 text-primary px-2 py-1 rounded-md text-xs whitespace-nowrap">
                                        {char}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            {evaluationData.feedback_data.corisco_satisfaction_rating && (
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm">Satisfação com a Corisco</span>
                                <span className="font-semibold text-primary">{evaluationData.feedback_data.corisco_satisfaction_rating}/10</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-muted-foreground">
                  <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Esta avaliação ainda não foi preenchida</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ações */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Ações de Avaliação
              </h3>
              
              {/* Ações para o avaliador */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => gerarLinkAvaliacao(avaliacao.evaluator_type)}
                  className="w-full justify-start"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar Link para {avaliacao.evaluator_type === 'freelancer' ? 'Freelancer' : 'Agência'}
                </Button>
                {generatedLinks[avaliacao.evaluator_type] && (
                  <div className="mt-2 p-2 bg-muted rounded text-xs">
                    <p className="text-muted-foreground mb-1">Link gerado:</p>
                    <input 
                      type="text" 
                      value={generatedLinks[avaliacao.evaluator_type]} 
                      readOnly 
                      className="w-full p-1 text-xs border rounded bg-background"
                      onClick={(e) => e.currentTarget.select()}
                    />
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => enviarEmailAvaliacao(avaliacao.evaluator_type)}
                  disabled={avaliacao.evaluator_type === 'agency'}
                  className={`w-full justify-start ${
                    avaliacao.evaluator_type === 'agency' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar Email para {avaliacao.evaluator_type === 'freelancer' ? 'Freelancer' : 'Agência'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
